import { Menu as BaseMenu } from '@base-ui/react/menu'
import type React from 'react'
import type { FC, HTMLAttributes, ReactNode } from 'react'
import { createContext, useContext } from 'react'
import { isMenuDismissalReason } from './menu-dismissal.js'
import styles from './menu.module.css'

/** Tracks whether we're inside a Menu.Root so MenuItem can self-wrap when standalone */
const MenuContext = createContext(false)

export interface MenuContentProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Positioning anchor override. Omit to anchor to the `<MenuTrigger>`; pass an
   * element to position elsewhere (e.g. a row opened by its context-menu gesture).
   */
  anchorEl?: Element | null
  children?: ReactNode
}

/**
 * The styled popup of a menu: Portal → Positioner → Popup with the shared menu
 * styling and modal z-index. Use inside the canonical `<Menu>` alongside a
 * `<MenuTrigger>`. The `Menu` wrapper uses the same structure internally for the
 * legacy controlled form.
 */
export const MenuContent: FC<MenuContentProps> = ({ anchorEl, className, children, style, id, ...rest }) => {
  const classes = [styles.menu, className].filter(Boolean).join(' ')

  return (
    <MenuContext.Provider value={true}>
      <BaseMenu.Portal>
        <BaseMenu.Positioner anchor={anchorEl ?? undefined} style={{ zIndex: 'var(--tuna-zIndex-modal)' as never }}>
          <BaseMenu.Popup className={classes} style={style} id={id} {...rest}>
            {children}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </MenuContext.Provider>
  )
}

export type MenuTriggerProps = React.ComponentProps<typeof BaseMenu.Trigger>

/**
 * A button that opens the menu. Thin styled wrapper of Base UI's `Menu.Trigger`;
 * supports the `render` prop so consumers supply their own button, e.g.
 * `<MenuTrigger render={<IconButton aria-label="settings"><MoreVertIcon /></IconButton>} />`.
 * Must be rendered inside the canonical `<Menu>` (uncontrolled, no `open`/`anchorEl`).
 */
export const MenuTrigger: FC<MenuTriggerProps> = props => <BaseMenu.Trigger {...props} />

export interface MenuProps {
  /**
   * Controlled open state. In the canonical form (no `anchorEl`, a `<MenuTrigger>`
   * in the tree) this is optional and pairs with `onOpenChange` — use it only when
   * something other than the trigger must open the menu (e.g. a right-click).
   * With `anchorEl` it selects the deprecated legacy form.
   */
  open?: boolean
  /** Canonical form: reports every open/close, whether or not `open` is controlled. */
  onOpenChange?: (open: boolean) => void
  /** @deprecated Legacy controlled form — see `anchorEl`. */
  onClose?: () => void
  /**
   * @deprecated Passing `anchorEl` (even `null`) selects the legacy controlled
   * form. Prefer the canonical trigger-based composition:
   * `<Menu><MenuTrigger … /><MenuContent>…</MenuContent></Menu>`. Submenus
   * require the canonical form. The legacy form keeps the dismissal allowlist
   * guard for the duration of the migration (GH #1496).
   */
  anchorEl?: HTMLElement | null
  /** @deprecated Legacy controlled form — see `anchorEl`. */
  anchorOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' | 'center' }
  className?: string
  id?: string
  style?: React.CSSProperties
  children?: ReactNode
  /** @deprecated Ignored - kept for MUI compat */
  keepMounted?: boolean
  /** @deprecated Ignored - kept for MUI compat */
  MenuListProps?: Record<string, unknown>
}

/**
 * Canonical (preferred) form — uncontrolled, with the trigger in the tree:
 *
 * ```tsx
 * <Menu>
 *   <MenuTrigger render={<IconButton aria-label="settings"><MoreVertIcon /></IconButton>} />
 *   <MenuContent>
 *     <MenuItem>…</MenuItem>
 *     <Submenu>…</Submenu>
 *   </MenuContent>
 * </Menu>
 * ```
 *
 * `onOpenChange` reports open state; `open` may additionally be controlled when
 * something besides the trigger opens the menu (the trigger stays in the tree).
 *
 * Legacy (deprecated) form — selected by passing `anchorEl`; controlled via
 * `open`/`anchorEl`/`onClose`. Kept working during the migration (GH #1496) with
 * the dismissal allowlist guard. Submenus require the canonical form.
 */
export const Menu: FC<MenuProps> = ({
  open,
  onOpenChange,
  onClose,
  anchorEl,
  className,
  children,
  style,
  id,
  keepMounted: _keepMounted,
  MenuListProps,
}) => {
  // Canonical form: no `anchorEl` — the consumer's <MenuTrigger> + <MenuContent>
  // live in the tree and Base UI owns the floating-tree state machine.
  if (anchorEl === undefined) {
    return (
      <BaseMenu.Root open={open} onOpenChange={o => onOpenChange?.(o)}>
        {children}
      </BaseMenu.Root>
    )
  }

  const classes = [styles.menu, className].filter(Boolean).join(' ')

  return (
    <MenuContext.Provider value={true}>
      <BaseMenu.Root
        open={open}
        onOpenChange={(o, eventDetails) => {
          /**
           * Forward only GENUINE dismissals to the consumer's `onClose`. Base UI
           * emits spurious `onOpenChange(false)` on the root during submenu
           * lifecycle — `sibling-open` on submenu open (GH #1474) and
           * `trigger-hover` on submenu close — which tore the whole menu down
           * mid-navigation. See menu-dismissal.ts for the allowlist rationale.
           */
          if (!o && isMenuDismissalReason(eventDetails.reason, eventDetails.event)) {
            onClose?.()
          }
        }}
      >
        <BaseMenu.Portal>
          <BaseMenu.Positioner anchor={anchorEl ?? undefined} style={{ zIndex: 'var(--tuna-zIndex-modal)' as never }}>
            <BaseMenu.Popup className={classes} style={style} id={id} {...MenuListProps}>
              {children}
            </BaseMenu.Popup>
          </BaseMenu.Positioner>
        </BaseMenu.Portal>
      </BaseMenu.Root>
    </MenuContext.Provider>
  )
}

export interface MenuItemProps extends HTMLAttributes<HTMLDivElement> {
  selected?: boolean
  disabled?: boolean
  dense?: boolean
  value?: string | number
  /** Forwarded to BaseMenu.Item. When false, clicking the item does not close the parent menu. Defaults to true. */
  closeOnClick?: boolean
  ref?: React.Ref<HTMLElement>
}

const menuItemClasses = (className?: string, selected?: boolean, _disabled?: boolean) =>
  [styles.item, selected ? styles.selected : undefined, className].filter(Boolean).join(' ')

/** MenuItem that renders as BaseMenu.Item inside a Menu, or a plain div when standalone */
export const MenuItem: FC<MenuItemProps> = ({
  selected,
  disabled,
  value,
  className,
  onClick,
  onKeyDown,
  dense: _dense,
  ref,
  ...rest
}) => {
  const insideMenu = useContext(MenuContext)

  if (insideMenu) {
    return (
      <BaseMenu.Item
        ref={ref}
        className={menuItemClasses(className, selected, disabled)}
        disabled={disabled}
        data-value={value}
        onClick={disabled ? undefined : onClick}
        {...rest}
      />
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.currentTarget.click()
    }
    onKeyDown?.(e)
  }

  return (
    <div
      ref={ref as React.Ref<HTMLDivElement>}
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      className={menuItemClasses(className, selected, disabled)}
      aria-disabled={disabled || undefined}
      data-value={value}
      onClick={disabled ? undefined : onClick}
      onKeyDown={disabled ? undefined : handleKeyDown}
      {...rest}
    />
  )
}

/**
 * Groups the parts of a flyout submenu. Thin re-export of Base UI's SubmenuRoot,
 * which owns all keyboard nav, ARIA, focus restoration, hover-open delay, and
 * edge-flipping. Requires the canonical `<Menu>` composition (uncontrolled Root +
 * `<MenuTrigger>`); the `#root`-scoped `data-base-ui-inert` CSS rule keeps the
 * parent menu hoverable for pointer travel into the flyout.
 */
export const Submenu: FC<React.ComponentProps<typeof BaseMenu.SubmenuRoot>> = props => (
  <BaseMenu.SubmenuRoot {...props} />
)

export interface SubmenuTriggerProps extends HTMLAttributes<HTMLDivElement> {
  disabled?: boolean
  ref?: React.Ref<HTMLElement>
}

/** A menu row that opens a submenu. Renders Base UI's SubmenuTrigger with the shared item style plus a chevron. */
export const SubmenuTrigger: FC<SubmenuTriggerProps> = ({ className, disabled, ref, ...rest }) => (
  <BaseMenu.SubmenuTrigger
    ref={ref}
    disabled={disabled}
    className={[styles.item, styles.submenuTrigger, className].filter(Boolean).join(' ')}
    {...rest}
  />
)

export interface SubmenuContentProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode
}

/** The popup panel for a submenu. Mirrors the top-level Menu's Portal → Positioner → Popup structure. */
export const SubmenuContent: FC<SubmenuContentProps> = ({ className, children, style, id, ...rest }) => {
  const classes = [styles.menu, className].filter(Boolean).join(' ')

  return (
    <MenuContext.Provider value={true}>
      <BaseMenu.Portal>
        <BaseMenu.Positioner style={{ zIndex: 'var(--tuna-zIndex-modal)' as never }}>
          <BaseMenu.Popup className={classes} style={style} id={id} {...rest}>
            {children}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </MenuContext.Provider>
  )
}
